import React, { useEffect, useState } from 'react';
import { Router, Redirect, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { isTablet, isMobile } from 'react-device-detect';
import styled from 'styled-components';

import Layout from './components/Layout';
import Auth from './modules/Auth';
import Admin from './modules/Admin';
import Spinner from './components/Spinner';
import { PositionModal } from './components/PositionModal';
import { useSaveUserPosition } from './components/PositionModal/usePositionModal';

import DistributionWrapper from './modules/Distribution';
import DistributionStorePage from './modules/Distribution/pages/Store';
import V2App from './v2/V2App';
import { DistributionAnalysis } from './v2/pages/DistributionAnalysis';
import GlobalViewContainer from 'modules/GlobalView';
import OnlineSalesPremiumOverview from './modules/OnlineSales/Overview';
import OnlineSalesPremiumDetailed from './modules/OnlineSales/Detailed';
import PrimeurDirect from './modules/PrimeurCampaign/pages/Direct';
import PrimeurRecap from './modules/PrimeurCampaign/pages/Recap';
import PrimeurPreparation from './modules/PrimeurCampaign/pages/Preparation';
import NotesWineExpertRating from './modules/Notes/pages/WineExpertRating';
import NotesComparison from './modules/Notes/pages/Comparison';
import NotesAllNotes from './modules/Notes/pages/AllNotes';
import SynthesisDistribution from './modules/SynthesisDistribution';
import SynthesisDistributionDetail from './modules/SynthesisDistribution/pages/details';
import PricesCompare from './modules/Prices/pages/compare';
import Auction from './modules/Auction';
import NegoceSubmissions from './modules/Negoce/pages/Submissions';
import NegoceSynthesis from './modules/Negoce/pages/Synthesis';
import NegocePresentation from './modules/Negoce/pages/Presentation';
import NegocePilotageInterne from './modules/Negoce/pages/PilotageInterne';
import NegoceRawData from './modules/Negoce/pages/RawData';
import Dashboard from './modules/Dashboard';
import Travel from './modules/Travel';
import ConnectAsClient from './modules/ConnectAsClient';

import PricesEvolution from './modules/Prices/pages/evolution';
import PricesPrice from './modules/Prices/pages/price';
import MarketNotebooks from './modules/MarketNotebooks';
import MediaPage from './modules/Media/pages/MediaPage';
import MediaArticleView from './modules/Media/pages/ArticleView';
import LegalNotice from './modules/LegalNotice';

import TasterSubscriptions from './modules/TasterSubscription';
import ResetPassword from './modules/ResetPassword';
import Performance from './modules/Performance';

import {
  onAutoLogin,
  getLanguageUser,
  setLanguageLocale
} from './actions/auth';
import {
  DISTRIBUTION,
  COMMUNICATION,
  PRICES,
  TRAVEL,
  NEGOCE,
  CARNETS_MARCHE,
  GLOBAL_DATA_VIEW
} from './constants/routes';
import history from './history';
import { isUserServicesList } from './utils/routeAccess';
import {
  showDistributionAnalysis,
  showPerformance,
  showV2Experience
} from './utils/personalisation';
import { useUserMe } from './apiTyped/hooks/users.query';

const PrivateRoute = ({
  isAuthenticated,
  isEnable = true,
  redirectDashboard,
  redirectNegociant = false,
  component: Component,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      if (!isAuthenticated) {
        return <Redirect to="/" />;
      }
      if (!isEnable) {
        if (redirectNegociant) {
          return <Redirect to="/negociants-data/submissions" />;
        }
        return <Redirect to={redirectDashboard ? '/dashboard' : '/'} />;
      }
      return <Component {...props} />;
    }}
  />
);

const Landscape = styled.div`
  ${(props) =>
    props.theme.devices.isTablet
      ? `@media only screen and (orientation:portrait) and (max-width: 1250px) {
      transform: rotate(-90deg);
      transform-origin: left top;
      width: 100vh;
      overflow-x: hidden;
      position: absolute;
      top: 100%;
      left: 0;
    }`
      : null}
`;

class AppRouter extends React.Component {
  isMobileWidth() {
    const WIDTH = window.innerWidth;
    return WIDTH < 845;
  }

  renderSynthesis() {
    return isMobile && this.isMobileWidth()
      ? SynthesisDistributionDetail
      : SynthesisDistribution;
  }

  render() {
    const { isAuthenticated, user } = this.props;
    const NEGOCE_ONLY =
      (isUserServicesList(user.services, NEGOCE.negociant) ||
        isUserServicesList(user.services, NEGOCE.audit_de_place)) &&
      !isUserServicesList(user.services, DISTRIBUTION.distribution) &&
      !isUserServicesList(user.services, COMMUNICATION.communication) &&
      !isUserServicesList(user.services, GLOBAL_DATA_VIEW.global_data_view) &&
      !isUserServicesList(user.services, PRICES.prices_analysis) &&
      !isUserServicesList(user.services, TRAVEL.travel);

    return (
      <Switch>
        <Route
          exact={true}
          path="/"
          component={(props) => <Auth {...props} />}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          redirectDashboard={true}
          path="/user-space/subscriptions"
          component={TasterSubscriptions}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          redirectNegociant={true}
          isEnable={!NEGOCE_ONLY}
          path="/dashboard"
          component={Dashboard}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          redirectDashboard={true}
          path="/connect-client/:token"
          component={ConnectAsClient}
        />

        <Route
          exact={true}
          path="/password-reset/confirm/:uid/:token"
          component={ResetPassword}
        />

        <Route exact={true} path="/mentions-legales" component={LegalNotice} />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          redirectDashboard={true}
          path="/admin"
          component={Admin}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          isEnable={isUserServicesList(user.services, COMMUNICATION.notes)}
          redirectDashboard={true}
          path="/communication/notes"
          component={NotesAllNotes}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          isEnable={
            isUserServicesList(user.services, COMMUNICATION.notes) &&
            user.client.wxrPanels.length > 0
          }
          redirectDashboard={true}
          path="/communication/wine-expert-rating"
          component={NotesWineExpertRating}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          isEnable={
            isUserServicesList(user.services, COMMUNICATION.notes) &&
            user.client.wxrPanels.length > 0
          }
          redirectDashboard={true}
          path="/communication/compare"
          component={NotesComparison}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          exact={true}
          isEnable={isUserServicesList(user.services, COMMUNICATION.press)}
          redirectDashboard={true}
          strict
          path="/communication/media/"
          component={MediaPage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, COMMUNICATION.press)}
          exact={true}
          path="/communication/media/article/:articleId/"
          component={MediaArticleView}
        />

        <Redirect from="/communication" to="/communication/media/" />

        <Redirect from="/communication/media" to="/communication/media/" />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, PRICES.prices_analysis)}
          exact={true}
          path="/prices/evolution"
          component={PricesEvolution}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, PRICES.prices_analysis)}
          exact={true}
          path="/prices/price"
          component={PricesPrice}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, PRICES.prices_analysis)}
          exact={true}
          path="/prices/compare"
          component={PricesCompare}
        />

        <Redirect from="/prices" to="/prices/price" />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants"
          component={DistributionWrapper}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants/points-of-sale"
          component={DistributionWrapper}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants/price"
          component={DistributionWrapper}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants/vintages"
          component={DistributionWrapper}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants/raw-data"
          component={DistributionWrapper}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.restaurants_caves
          )}
          exact={true}
          path="/distribution/restaurants/store/:storeId"
          component={DistributionStorePage}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            GLOBAL_DATA_VIEW.global_data_view
          )}
          exact={true}
          path="/distribution/global-view"
          component={GlobalViewContainer}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.primeur_campaign
          )}
          exact={true}
          path="/distribution/primeur"
          component={PrimeurDirect}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.primeur_campaign
          )}
          exact={true}
          path="/distribution/primeur/recap"
          component={PrimeurRecap}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.primeur_campaign
          )}
          exact={true}
          path="/distribution/primeur/preparation"
          component={PrimeurPreparation}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, DISTRIBUTION.synthesis)}
          exact={true}
          path="/distribution/synthesis"
          component={this.renderSynthesis()}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, DISTRIBUTION.synthesis)}
          exact={true}
          path="/distribution/synthesis/detail"
          component={SynthesisDistributionDetail}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.online_sales
          )}
          exact={true}
          path="/distribution/online-sales/overview"
          component={OnlineSalesPremiumOverview}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            CARNETS_MARCHE.carnets_marche
          )}
          exact={true}
          path="/notebooks"
          component={MarketNotebooks}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.online_sales
          )}
          exact={true}
          path="/distribution/online-sales/detailed"
          component={OnlineSalesPremiumDetailed}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={isUserServicesList(user.services, TRAVEL.travel)}
          path="/travel"
          component={Travel}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/distribution/auction"
          component={Auction}
          isEnable={isUserServicesList(
            user.services,
            DISTRIBUTION.auction_sales
          )}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          isEnable={
            showDistributionAnalysis(user.client.id) &&
            isUserServicesList(user.services, GLOBAL_DATA_VIEW.global_data_view)
          }
          path="/distribution/analysis"
          component={DistributionAnalysis}
        />

        <Redirect from="/distribution" to="/distribution/restaurants" />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          path="/performance/restaurants/:restaurantId"
          component={Performance}
          isEnable={showPerformance(user.client.id)}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          path="/performance/restaurants"
          component={Performance}
          isEnable={showPerformance(user.client.id)}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          redirectDashboard={true}
          exact={true}
          path="/performance"
          component={Performance}
          isEnable={showPerformance(user.client.id)}
        />

        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/negociants-data/submissions"
          component={NegoceSubmissions}
          isEnable={
            isUserServicesList(user.services, NEGOCE.audit_de_place) ||
            isUserServicesList(user.services, NEGOCE.negociant)
          }
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/negociants-data/synthesis"
          component={NegoceSynthesis}
          isEnable={isUserServicesList(user.services, NEGOCE.audit_de_place)}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/negociants-data/kpis"
          component={NegocePilotageInterne}
          isEnable={isUserServicesList(user.services, NEGOCE.audit_de_place)}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/negociants-data/presentation"
          component={NegocePresentation}
          isEnable={isUserServicesList(user.services, NEGOCE.audit_de_place)}
        />
        <PrivateRoute
          isAuthenticated={isAuthenticated}
          path="/negociants-data/raw-data"
          component={NegoceRawData}
          isEnable={isUserServicesList(user.services, NEGOCE.audit_de_place)}
        />
        <Redirect from="/stats" to="/negociants-data/submissions" />
        <Redirect from="/negociants-data" to="/negociants-data/submissions" />
      </Switch>
    );
  }
}

const Index = (props) => {
  const [canLoadUser, setCanLoadUser] = React.useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [positionModalDismissed, setPositionModalDismissed] = useState(false);

  const queryClient = useQueryClient();
  const { user, isLoading: isLoadingUser } = useUserMe(canLoadUser);
  const savePosition = useSaveUserPosition();

  useEffect(() => {
    const init = async () => {
      await props.autoLogin();
      const defaultLanguage = getLanguageUser(null);
      await props.setLanguageLocale(defaultLanguage);
    };
    init();
  }, []);

  // // If admin use log as client, the token may change
  // // but react query don't see changing, you have to force the user to reload
  useEffect(() => {
    if (!props.token) {
      const clear = async () => queryClient.removeQueries();
      clear();
      return;
    }

    const clearQueriesCached = async () => {
      await queryClient.removeQueries();
      setCanLoadUser(true);
      setIsLoadingPage(true);
    };
    clearQueriesCached();
  }, [props.token, props.clientToken]);

  useEffect(() => {
    const init = async () => {
      const userLanguage = getLanguageUser(user);
      if (userLanguage !== null) {
        await props.setLanguageLocale(userLanguage);
        setIsLoadingPage(false);
      }
    };
    init();
  }, [user]);

  const showPositionModal =
    props.isAuthenticated &&
    user.id !== null &&
    !user.job &&
    !positionModalDismissed &&
    !isLoadingPage &&
    !isLoadingUser;

  return (
    <Landscape isTablet={isTablet}>
      <Router history={history}>
        {props.isAuthenticated === undefined ||
        (props.isAuthenticated && user.id === null) ? (
          <Layout user={user} isLoading={isLoadingPage || isLoadingUser}>
            <Spinner loading={true} />
          </Layout>
        ) : (
          <Switch>
            <PrivateRoute
              isAuthenticated={props.isAuthenticated}
              isEnable={showV2Experience(user.client.id)}
              path="/v2"
              component={(routeProps) => <V2App {...routeProps} user={user} />}
            />
            <Route>
              <Layout user={user} isLoading={isLoadingPage || isLoadingUser}>
                <AppRouter
                  {...props}
                  isAuthenticated={props.isAuthenticated}
                  user={user}
                  loading={isLoadingPage && isLoadingUser}
                />
              </Layout>
            </Route>
          </Switch>
        )}
      </Router>
    </Landscape>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.auth.token,
    clientToken: state.auth.clientToken,
    isAuthenticated: state.auth.isAuthenticated,
    locale: state.i18n.locale
  };
};

const mapDispatchToProps = (dispatch) => ({
  autoLogin: async () => {
    await dispatch(onAutoLogin());
  },
  setLanguageLocale: async (locale) => {
    await dispatch(setLanguageLocale(locale));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Index);
